import { apiFetch } from "../utils/apiFetch";
import { saveDisplayUser, takePendingInvitation } from "../utils/authStorage";
import { verifyBrowserSession } from "../utils/verifyBrowserSession";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../context/AuthContext";
import { FaRegCircleCheck } from "react-icons/fa6";
import { emailRegex, usernameRegex, passwordRegex } from "../utils/validation";

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPasword] = useState("");
  const navigate = useNavigate();
  const { setUserId, setUserInfo } = useAuth();
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [error, setError] = useState("");
  const requirements = [
    ["At least 8 characters", password.length >= 8],
    ["One uppercase letter", /[A-Z]/.test(password)],
    ["One lowercase letter", /[a-z]/.test(password)],
    ["One number", /\d/.test(password)],
    ["One special character", /[^a-zA-Z0-9]/.test(password)],
  ];

  const hanldeClick = async () => {
    if (loadingSpinner) return;
    setError("");
    if (name.trim() === "" || email.trim() === "" || password.trim() === "") {
      setError("All fields are required.");
      return;
    }
    if (
      !emailRegex.test(email) ||
      !usernameRegex.test(name) ||
      !passwordRegex.test(password)
    ) {
      setError("Invalid email, username or password.");
      return;
    }
    setLoadingSpinner(true);
    try {
      const res = await apiFetch(`${process.env.API_URL}/api/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: name, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message + `: ${res.status}`);
      }

      const user = await verifyBrowserSession();
      saveDisplayUser(user);
      setUserId(user.id);
      setUserInfo(user);

      const pendingInvitation = takePendingInvitation();
      setLoadingSpinner(false);
      if (pendingInvitation) {
        navigate(`/join${pendingInvitation}`);
      } else navigate("/explore", { replace: true });
    } catch (err) {
      setLoadingSpinner(false);
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 mb-2"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-message-square h-7 w-7 text-white"
                data-fg-djkv6="2.43:2.7007:/src/app/components/LoginPage.tsx:29:15:1189:48:e:MessageSquare::::::BITa"
                data-fgid-djkv6=":rdv:"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <span className="text-2xl font-bold text-gray-900">ChatSpace</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">
            Create your account
          </h1>
          <p className="text-gray-600 mt-2">
            Start collaborating with your team today
          </p>
        </div>
        <div className="auth-card bg-white rounded-lg border border-gray-300 shadow-xl p-8">
          <form className="space-y-5" onSubmit={event => { event.preventDefault(); hanldeClick(); }}>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="space-y-2">
              <label
                htmlFor="username"
                className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
              >
                Full Name
              </label>
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-user absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <input
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive pl-10"
                  type="text"
                  id="username"
                  name="username"
                  value={name}
                  placeholder="Full Name"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              {name && !usernameRegex.test(name) && (
                <p className="text-xs text-red-600 font-medium ml-1">
                  Invalid Username
                </p>
              )}
              <p className="text-xs text-gray-500 flex gap-1 items-center">
                3 to 20 characters, only letters, numbers, and _
              </p>
            </div>
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
              >
                Email
              </label>
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-mail absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
                <input
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive pl-10"
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  placeholder="you@company.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {email && !emailRegex.test(email) && (
                <p className="text-xs text-red-600 font-medium ml-1">
                  Invalid email
                </p>
              )}
            </div>
            <div className="space-y-2 mb-10">
              <label
                htmlFor="password"
                className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
              >
                Password
              </label>
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-lock absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                >
                  <rect
                    width="18"
                    height="11"
                    x="3"
                    y="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive pl-10"
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Create a strong password"
                  aria-describedby="password-requirements"
                  value={password}
                  onChange={(e) => setPasword(e.target.value)}
                />
              </div>
              {password && !passwordRegex.test(password) && (
                <p className="text-xs text-red-600 font-medium ml-1">
                  Invalid password
                </p>
              )}
              <ul id="password-requirements" aria-label="Password requirements" className="space-y-1.5 text-xs">
                {requirements.map(([label, met]) => <li key={label} data-satisfied={met} className={`flex items-center gap-2 ${met ? "text-green-700" : "text-gray-500"}`}>
                  <FaRegCircleCheck aria-hidden="true" /><span>{label}</span><span className="sr-only">{met ? " satisfied" : " not yet satisfied"}</span>
                </li>)}
              </ul>
            </div>
            <button type="submit" aria-busy={loadingSpinner}
              disabled={loadingSpinner || !emailRegex.test(email) || !usernameRegex.test(name) || !passwordRegex.test(password)}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md border border-orange-600 bg-orange-700 px-4 text-base font-semibold text-white shadow-sm hover:bg-orange-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingSpinner && (
                <svg
                  className="mr-3 size-5 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                  />
                  <path
                    d="M22 12a10 10 0 0 1-10 10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-75"
                  />
                </svg>
              )}
              Sign Up
            </button>
          </form>
          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{" "}
            <Link to="/login"
              className="text-blue-600 font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
